import './design-tokens/tokens.css'
import './assets/base.css'
import 'primeicons/primeicons.css'

import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import ConfirmationService from 'primevue/confirmationservice'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import { createApp } from 'vue'

import App from './App.vue'
import { i18n } from './i18n'
import { queryClient } from './lib/query-client'
import { primevueThemeConfig } from './plugins/primevue-theme'
import { router } from './router'

const app = createApp(App)
const pinia = createPinia()

// Order matters: Pinia must install before Router, because
// router/guards.ts calls useSessionStore()/useRbacStore() inside the
// beforeEach guard (see vue-pinia-best-practices: "No Active Pinia" gotcha).
app.use(pinia)
app.use(router)
app.use(i18n)
app.use(PrimeVue, { theme: primevueThemeConfig })
// Added alongside CRM-6's agent inbox — the first screens in this console
// needing async-action feedback (toasts on reply/assign/transition) and a
// destructive-action confirm (bulk close). Registered once, app-wide, so
// any future screen can call `useToast()`/`useConfirm()` without its own
// plugin wiring; the actual <Toast>/<ConfirmDialog> roots are mounted once
// in AppShellLayout.vue, mirroring how StepUpDialog is mounted there.
app.use(ToastService)
app.use(ConfirmationService)
// Provided at the app root, deliberately separate from Pinia — Vue Query
// owns server cache/invalidation, Pinia owns session/RBAC/UI prefs. See
// lib/query-client.ts.
app.use(VueQueryPlugin, { queryClient })

if (import.meta.env.DEV) {
  // Debug-only hooks for manual verification (e.g. driving the guard/shell
  // from the browser console or Playwright without a real backend).
  // Dead-code-eliminated from production builds since `import.meta.env.DEV`
  // is statically `false` there.
  const debugWindow = window as unknown as {
    __pinia__?: typeof pinia
    __router__?: typeof router
    __requestStepUp__?: () => Promise<boolean>
  }
  debugWindow.__pinia__ = pinia
  debugWindow.__router__ = router
  // MS-1 ships no finance/entitlement screen that calls
  // useStepUpStore().request() yet (see stores/step-up.ts's doc comment) —
  // this hook lets the real dialog + POST /auth/step-up round trip be
  // driven and verified before a future milestone's screen is the one
  // actually calling it.
  debugWindow.__requestStepUp__ = async () => {
    const { useStepUpStore } = await import('@/stores/step-up')
    return useStepUpStore().request()
  }
}

app.mount('#app')
